import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Factory, Copy } from "@phosphor-icons/react";
import RecipeCombobox from "@/components/RecipeCombobox";
import InventoryItemCombobox from "@/components/InventoryItemCombobox";

const STATUS_TONE = {
  planned: "bg-slate-100 text-slate-700",
  reserved: "bg-amber-50 text-amber-700",
  in_progress: "bg-sky-50 text-sky-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

export default function ProductionRuns() {
  const [rows, setRows] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [r, rec, it] = await Promise.all([
        api.get("/inventory/production-runs"),
        api.get("/inventory/recipes"),
        api.get("/inventory/items", { params: { category: "finished_goods" } }),
      ]);
      setRows(r.data || []);
      setRecipes(rec.data || []);
      setItems(it.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (form) => {
    try {
      const { data } = await api.post("/inventory/production-runs", {
        recipe_id: form.recipe_id,
        batch_size: Number(form.batch_size),
        fg_item_id: form.fg_item_id || "",
        notes: form.notes || "",
      });
      setDialogOpen(false);
      navigate(`/inventory/production-runs/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create run");
    }
  };

  return (
    <div className="space-y-5" data-testid="inv-production-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Inventory · Production
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Production Runs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Plan → reserve → issue → complete. FIFO/FEFO auto-deduct, variance tracked.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/inventory/requisitions"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 shadow-sm"
            data-testid="req-goto-btn"
            title="Runs are created via approved requisitions"
          >
            <Plus size={14} weight="bold" /> New Requisition
          </Link>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900" data-testid="run-requisition-notice">
        <strong>Note:</strong> Production Runs are created automatically when the Store team approves a Requisition. Use the <Link to="/inventory/requisitions" className="underline font-semibold">Requisitions</Link> page to raise a new batch.
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Code", "Recipe", "Batch", "FG Output", "Status", "Yield", "Created", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-12 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                  <Factory size={24} weight="duotone" />
                </div>
                <div className="text-sm text-slate-700 font-medium">No production runs yet.</div>
                <div className="text-xs text-slate-500 mt-1">Click &quot;New Production Run&quot; to chain a recipe into a planned batch.</div>
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`run-row-${r.id}`}>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">
                  {r.code}
                  {r.requisition_code && (
                    <Link
                      to={`/inventory/requisitions/${r.requisition_id}`}
                      className="block mt-0.5 text-[10px] text-emerald-700 hover:text-emerald-900 font-medium"
                      data-testid={`run-req-link-${r.id}`}
                      title="Open originating requisition"
                    >
                      ← {r.requisition_code}
                    </Link>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.recipe_name}</td>
                <td className="px-4 py-3 text-sm tabular-nums">{r.batch_size} kg</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.fg_item_name || "—"}</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${STATUS_TONE[r.status]}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-sm tabular-nums">{r.actual_yield > 0 ? (
                  <span className={r.yield_variance_pct < 0 ? "text-amber-700" : "text-emerald-700"}>
                    {r.actual_yield} kg ({r.yield_variance_pct > 0 ? "+" : ""}{r.yield_variance_pct}%)
                  </span>
                ) : "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    {r.status === "completed" && (
                      <button
                        onClick={() => navigate(`/inventory/production-wizard?clone=${r.id}`)}
                        className="text-xs font-medium text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1"
                        data-testid={`run-clone-${r.id}`}
                        title="Pre-fill the wizard with this recipe + batch size"
                      >
                        <Copy size={12} weight="bold" /> Clone
                      </button>
                    )}
                    <Link
                      to={`/inventory/production-runs/${r.id}`}
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-900"
                      data-testid={`run-open-${r.id}`}
                    >
                      Open →
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialogOpen && (
        <NewRunDialog recipes={recipes} items={items} onClose={() => setDialogOpen(false)} onSubmit={onCreate} />
      )}
    </div>
  );
}

function NewRunDialog({ recipes, items, onClose, onSubmit }) {
  const [form, setForm] = useState({ recipe_id: "", batch_size: 30, fg_item_id: "", notes: "" });
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4" data-testid="new-run-dialog">
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="bg-white rounded-lg shadow-xl w-full max-w-xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">New Production Run</h2>
          <div className="text-xs text-slate-500 mt-0.5">We&apos;ll snapshot the BOM from the recipe at this exact batch size.</div>
        </div>
        <div className="px-6 py-5 space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Recipe *</span>
            <RecipeCombobox
              recipes={recipes}
              value={form.recipe_id}
              onPick={(v) => setForm((f) => ({ ...f, recipe_id: v }))}
              testId="new-run-recipe"
              placeholder="Select recipe…"
              className="h-10"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Batch size (kg of finished product) *</span>
            <input required type="number" step="0.1" min="0.1" value={form.batch_size} onChange={(e) => setForm((f) => ({ ...f, batch_size: e.target.value }))} className={inp} data-testid="new-run-batch-size" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Finished-goods item (where to credit yield)</span>
            <InventoryItemCombobox
              items={items}
              value={form.fg_item_id}
              onPick={(v) => setForm((f) => ({ ...f, fg_item_id: v }))}
              testId="new-run-fg"
              placeholder="— Don't track FG stock —"
              className="h-10"
              showAvailability={false}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Notes</span>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inp} />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700" data-testid="new-run-submit">Plan Run</button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";
